import { api } from "@/modules/api";
import { CacheKey } from "@/modules/cache/constants";
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
  buildAsyncData,
} from "@/domains/async/AsyncData";
import { BaseError } from "@/domains/errors";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { DateTime, Duration } from "luxon";
import { IntervalObservable } from "@/store/common/IntervalObservable";
import { CacheStore } from "@/modules/cache";

type CachedAssetAccessToken = {
  token: string;
  expiresAt: string | null;
};

type FetchOptions = {
  force?: boolean;
};

export class AssetAccessTokenState {
  globalCache: CacheStore;
  token: string | null = null;
  status: AsyncData<void> = buildAsyncData<void>({});
  issuedAt: DateTime | null = null;
  expiresAt: DateTime | null = null;
  lastErrorMessage: string | null = null;

  private inflightRequest: Promise<string | null> | null = null;
  private hydrationPromise: Promise<void> | null = null;
  private refreshTimer: IntervalObservable | null = null;
  private refreshDisposer: (() => void) | null = null;

  constructor({ globalCache }: { globalCache: CacheStore }) {
    this.globalCache = globalCache;

    makeAutoObservable<AssetAccessTokenState, "inflightRequest" | "hydrationPromise">(this, {
      globalCache: false,
      inflightRequest: false,
      hydrationPromise: false,
    });

    this.hydrationPromise = this.hydrateFromCache();
    this.initializeRefreshTimer();
  }

  get isLoading() {
    return !!this.status.isLoading;
  }

  get isReady() {
    return !!this.token;
  }

  get hasError() {
    return !!this.lastErrorMessage;
  }

  reset = () => {
    this.token = null;
    this.issuedAt = null;
    this.expiresAt = null;
    this.lastErrorMessage = null;
    this.status = buildAsyncData<void>({});
    this.inflightRequest = null;
    this.hydrationPromise = null;
    void this.globalCache.del(CacheKey.AssetAccessToken);
    this.disposeRefreshTimer();
  };

  ensureToken = async (): Promise<string | null> => {
    this.initializeRefreshTimer();

    await this.awaitHydration();

    if (this.token && !this.isTokenExpired()) {
      return this.token;
    }

    try {
      return await this.fetch();
    } catch (unknownErr) {
      logger.warn({
        message: "[AssetAccessTokenState] ensureToken failed",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });

      return this.token;
    }
  };

  fetch = async ({ force = false }: FetchOptions = {}): Promise<string | null> => {
    this.initializeRefreshTimer();

    await this.awaitHydration();

    if (this.token && !force && !this.isTokenExpired()) {
      return this.token;
    }

    if (this.inflightRequest && !force) {
      return this.inflightRequest;
    }

    const request = (async () => {
      runInAction(() => {
        this.status = asyncDataLoading(this.status);
        this.lastErrorMessage = null;
      });

      try {
        const response = await api.post("/v2/assets/access-tokens", {});

        if (response.error) {
          throw new BaseError({
            message: "[AssetAccessTokenState] Failed to generate asset access token.",
            info: objectModule.safeAsJson(response),
          });
        }

        const responseBody = response.data;
        if (!responseBody) {
          throw new BaseError({
            message: "[AssetAccessTokenState] Missing response body while generating token.",
          });
        }

        const token = responseBody.asset_access_token;
        if (!token) {
          throw new BaseError({
            message: "[AssetAccessTokenState] asset_access_token missing from response.",
            info: objectModule.safeAsJson({ responseBody }),
          });
        }

        const parsedExpiresAt = responseBody.expires_at
          ? DateTime.fromISO(responseBody.expires_at).toUTC()
          : null;

        runInAction(() => {
          this.token = token;
          this.issuedAt = DateTime.now();
          this.expiresAt = parsedExpiresAt;
          this.status = asyncDataLoaded(this.status, undefined);
          this.lastErrorMessage = null;
        });

        await this.persistToken(token, parsedExpiresAt);

        return token;
      } catch (unknownErr) {
        const err =
          unknownErr instanceof BaseError
            ? unknownErr
            : new BaseError({
                message: "[AssetAccessTokenState] Unexpected error while generating token.",
                originalError: unknownErr as Error,
              });

        runInAction(() => {
          this.status = asyncDataFailed(this.status);
          this.lastErrorMessage = err.message;
        });

        logger.error({
          message: "[AssetAccessTokenState] fetch failed",
          info: {
            err: objectModule.safeErrorAsJson(err),
          },
        });

        throw err;
      } finally {
        runInAction(() => {
          this.inflightRequest = null;
        });
      }
    })();

    this.inflightRequest = request;

    return request;
  };

  regenerate = async (): Promise<string | null> => {
    return this.fetch({ force: true });
  };

  private isTokenExpired(): boolean {
    if (!this.expiresAt) {
      return false;
    }

    return this.expiresAt.toMillis() <= DateTime.now().toMillis();
  }

  private async hydrateFromCache(): Promise<void> {
    try {
      const cached = (await this.globalCache.get(
        CacheKey.AssetAccessToken
      )) as CachedAssetAccessToken | null;
      if (!cached) return;

      const expiresAt =
        cached.expiresAt !== null ? DateTime.fromISO(cached.expiresAt).toUTC() : null;

      if (expiresAt && !expiresAt.isValid) {
        await this.globalCache.del(CacheKey.AssetAccessToken);
        return;
      }

      if (expiresAt && expiresAt.toMillis() <= DateTime.now().toMillis()) {
        await this.globalCache.del(CacheKey.AssetAccessToken);
        return;
      }

      runInAction(() => {
        this.token = cached.token;
        this.expiresAt = expiresAt;
        this.issuedAt = DateTime.now();
        this.status = asyncDataLoaded(this.status, undefined);
        this.lastErrorMessage = null;
      });
    } catch (unknownErr) {
      logger.warn({
        message: "[AssetAccessTokenState] Failed to hydrate from cache",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });
    } finally {
      this.hydrationPromise = null;
    }
  }

  private async awaitHydration(): Promise<void> {
    if (!this.hydrationPromise) {
      return;
    }

    await this.hydrationPromise;
  }

  private async persistToken(token: string, expiresAt: DateTime | null): Promise<void> {
    try {
      await this.globalCache.set(CacheKey.AssetAccessToken, {
        token,
        expiresAt: expiresAt ? expiresAt.toUTC().toISO() : null,
      });
    } catch (unknownErr) {
      logger.warn({
        message: "[AssetAccessTokenState] Failed to persist token to cache",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });
    }
  }

  private initializeRefreshTimer() {
    if (this.refreshTimer) {
      return;
    }

    this.refreshTimer = new IntervalObservable({
      duration: Duration.fromObject({ hours: 4 }),
      jitter: Duration.fromObject({ minutes: 5 }),
    });

    this.refreshDisposer = reaction(
      () => this.refreshTimer?.value,
      () => {
        void this.refreshIfExpiringSoon();
      },
      { fireImmediately: true }
    );
  }

  private disposeRefreshTimer() {
    this.refreshDisposer?.();
    this.refreshDisposer = null;
    this.refreshTimer?.dispose();
    this.refreshTimer = null;
  }

  private async refreshIfExpiringSoon(): Promise<void> {
    await this.awaitHydration();

    if (!this.token || !this.expiresAt) {
      return;
    }

    const refreshThreshold = DateTime.now().plus({ hours: 24 });
    if (this.inflightRequest || this.expiresAt > refreshThreshold) {
      return;
    }

    try {
      await this.fetch({ force: true });
    } catch (err) {
      logger.warn({
        message: "[AssetAccessTokenState] refreshIfExpiringSoon failed",
        info: {
          err: objectModule.safeErrorAsJson(err as BaseError),
        },
      });
    }
  }
}
